
import { useInstanceKey } from 'app/area/favourite/favourite-context';
import { FavouriteDefinition } from 'app/types-shared';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { actionCreators as caseListingActionCreators } from 'app/area/case-report/case-listing/actions';
import debounce from 'lodash/debounce';
import { debounceExecuteFavouriteWait } from 'app/constants';

export function useLoadPage(
  workingDefinition: FavouriteDefinition
) {

  const instanceKey = useInstanceKey();

  const {
    executeFavouriteDefinition
  } = useActionCreators();

  const debouncedExecute = useCallback(debounce((definition, startIndex, maxResults) => {
    executeFavouriteDefinition(definition, true, startIndex, maxResults, true);
  }, debounceExecuteFavouriteWait), [instanceKey]);

  const execute = useCallback((currentPage: number, maxOptionsPerPage: number) => {
    const startIndex = (currentPage - 1) * maxOptionsPerPage;
    debouncedExecute(workingDefinition, startIndex, maxOptionsPerPage);
  }, [workingDefinition, debouncedExecute, instanceKey]);

  const loadPage = useCallback((currentPage: number, maxOptionsPerPage: number) => {
    execute(currentPage, maxOptionsPerPage);
  }, [execute]);

  return {
    loadPage
  };
}

export function usePagination(
  totalNumberOfResults: number,
  maxOptionsPerPage: number,
  minimumOptionsNeededBeforePagination: number
) {
  const [currentPage, setCurrentPage] = useState(1);
  const numberOfPages = !!totalNumberOfResults ? Math.ceil(totalNumberOfResults / maxOptionsPerPage) : 1;
  const shouldPaginate = numberOfPages > 1 && totalNumberOfResults > minimumOptionsNeededBeforePagination;
  const startIndex = (currentPage - 1) * maxOptionsPerPage;

  const first = useCallback(() => { setCurrentPage(1) }, []);
  const previous = useCallback(() => { setCurrentPage(currentPage - 1) }, [currentPage]);
  const next = useCallback(() => { setCurrentPage(currentPage + 1) }, [currentPage]);
  const last = useCallback(() => { setCurrentPage(numberOfPages) }, [numberOfPages]);

  useEffect(() => {
    if (currentPage > numberOfPages) {
      setCurrentPage(1);
    }
  }, [currentPage, numberOfPages]);

  return {
    numberOfPages,
    shouldPaginate,
    currentPage,
    startIndex,
    first,
    previous,
    next,
    last
  };
}

export function useActionCreators() {
  const dispatch = useDispatch();
  const instanceKey = useInstanceKey();
  return useMemo(() => bindActionCreators(caseListingActionCreators(instanceKey), dispatch), [instanceKey]);
}
